import { module } from './module.js';
import { mapConfiguratorEvents } from './utils/map-configurator-events.js';
/**
 * Enables telemetry for a given module configurator.
 *
 * @param configurator - The module configurator instance to which telemetry should be attached.
 * @param options - Optional configuration for telemetry setup.
 * @param options.attachConfiguratorEvents - If true, attaches configurator events to the telemetry builder.
 * @param options.configure - An optional callback to further configure the telemetry builder. Can be synchronous or asynchronous.
 *
 * @remarks
 * This function adds telemetry configuration to the provided configurator. It supports attaching configurator events
 * and allows for additional custom configuration via the `configure` callback.
 *
 * @remarks
 * `attachConfiguratorEvents` allows for the automatic attachment of configurator events to the telemetry builder,
 * ensuring that relevant events are captured and processed. __NOTE__ if the configurator crashes before the telemetry
 * builder is fully initialized, some events may be missed.
 *
 * @example
 * ```typescript
 * import { enableTelemetry } from '@equinor/fusion-framework-module-telemetry';
 *
 * const configure = (configurator) => {
 *   enableTelemetry(configurator, {
 *     attachConfiguratorEvents: true,
 *     configure: (builder) => {
 *       builder.setAdapter('console', new ConsoleAdapter());
 *       builder.setMetadata(() => ({ appVersion: '1.0.0' }));
 *     },
 *   });
 * };
 * ```
 */
export const enableTelemetry = (
// biome-ignore lint/suspicious/noExplicitAny: must be any to support all module types
configurator, options) => {
    configurator.addConfig({
        module,
        configure: async (builder, ref) => {
            // Attach configurator events to the telemetry builder only when opted in
            if (options?.attachConfiguratorEvents) {
                builder.attachItems(mapConfiguratorEvents(configurator));
            }
            // Run the caller-supplied configure callback when one was provided
            if (options?.configure) {
                await Promise.resolve(options.configure(builder, ref));
            }
        },
    });
};
//# sourceMappingURL=enable-telemetry.js.map